import { useEffect } from 'react';
import { showProgressToast, dismissToast } from '@ui';
import { translate } from '@utils/i18n';
const NS = 'features.building_attachments.tab.progress_toast';
/** Retry-eligible statuses (mirrors `AddAttachmentsQueueItemStatus`'s own "never silently done"
 *  rule -- `'unconfirmed'` items count toward "failed" the same as `'error'` ones here). */
function isFailed(item) {
    return item.status === 'error' || item.status === 'unconfirmed';
}
/**
 * Drives the background-upload progress toast (task-22 / E2E-19, E2E-20, E2E-21) and the
 * navigate-away `beforeunload` warning Lee asked for alongside it. Reacts to queue state already
 * hoisted above `AddAttachmentsBrushaway` by `useAddAttachmentsUpload` (task-09's re-scope put
 * `useDirectUpload` one level above the organism specifically so the queue survives the drawer
 * closing -- see that hook's module doc) -- this hook never touches the upload queue itself, only
 * observes it.
 *
 * One toast at all times: `showProgressToast` is always called with the same stable id, so
 * react-hot-toast's own upsert-by-id updates it in place instead of stacking a new one (E2E-20).
 */
export function useUploadProgressToast({ buildingId, isOpen, items, isUploading, aggregateProgress, openDrawer }) {
    const toastId = `building-attachments-upload-progress-${buildingId}`;
    const total = items.length;
    const failed = items.filter(isFailed).length;
    const done = items.filter((item) => item.status === 'done').length;
    const remaining = total - done - failed;
    const hasFailures = failed > 0;
    // Drawer closed, queue non-empty, and (still uploading or left with failures) -- E2E-19.
    const shouldShow = !isOpen && total > 0 && (isUploading || hasFailures);
    // A stopped batch with failures reads as "review", not "still uploading" (E2E-21); while the
    // batch is actively running the uploading copy wins even if some items have already errored,
    // since `remaining` already excludes them.
    const showFailedVariant = hasFailures && !isUploading;
    useEffect(() => {
        if (!shouldShow) {
            dismissToast(toastId);
            return;
        }
        if (showFailedVariant) {
            showProgressToast({
                id: toastId,
                progress: aggregateProgress,
                variant: 'error',
                title: translate('failed', { namespace: NS, count: failed }),
                action: { label: translate('review_uploads', { namespace: NS }), onClick: openDrawer }
            });
        }
        else {
            showProgressToast({
                id: toastId,
                progress: aggregateProgress,
                variant: 'progress',
                title: translate('uploading', { namespace: NS, count: remaining, pct: aggregateProgress }),
                action: { label: translate('view_progress', { namespace: NS }), onClick: openDrawer }
            });
        }
    }, [shouldShow, showFailedVariant, remaining, failed, aggregateProgress, toastId, openDrawer]);
    // Dismiss on unmount so a page navigating away within the same React root (e.g. a client-side
    // route change, if this feature ever gains one) doesn't leave an orphaned toast behind.
    useEffect(() => () => dismissToast(toastId), [toastId]);
    // Navigate-away warning (Lee's other explicit ask, work-plans STATUS 2026-09-28): this app is
    // react-rails per-component mounts, not an SPA (see module doc in `useAddAttachmentsUpload.ts`
    // and task-22's own context) -- a full page navigation destroys the React root and kills any
    // in-flight XHRs. `beforeunload` is the only honest mitigation available; it is registered only
    // while a batch is actually running and removed as soon as it stops.
    useEffect(() => {
        if (!isUploading)
            return undefined;
        const handleBeforeUnload = (event) => {
            event.preventDefault();
            // Legacy browsers read the returned string; modern ones just check `defaultPrevented`.
            // eslint-disable-next-line no-param-reassign
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isUploading]);
}
